<template>
  <div class="bread-crumb" v-show="showBreadCrumb">
    <v-breadcrumbs :items="breadCrumbItems" divider=">">
      <template v-slot:item="{ item }">
        <v-breadcrumbs-item
          :href="item.href"
          :disabled="item.disabled"
          @click="dispWarning"
        >
          {{ item.text }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>
  </div>
</template>

<script>
import { ScreenId } from "@/js/value-objects/screen-id";
import { ScreenCrud } from "@/js/value-objects/screen-crud";

export default {
  data: () => ({
    showBreadCrumb: false
  }),

  computed: {
    breadCrumbItems() {
      const breadCrumbs = this.generateBreadCrumb();
      return breadCrumbs;
    }
  },

  methods: {
    /**
     * 設定されている場合、画面遷移時の警告を出す
     */
    dispWarning() {
      // 現在の画面が警告対象かのチェックをする
      // 通常パターン
      // 原子力災害関係施設状況一覧 & 更新パターン
      // 要配慮者施設状況一覧 & 更新パターン
      // OIL一覧 & 更新パターン
      if (
        (this.$route.path == "/user-authorization-operation" &&
          this.$screenOperationAuthority(ScreenId.SF1204, ScreenId.SF1208)
            .screenId == ScreenId.SF1208) ||
        (this.$route.path == "/facility-status/nuclear-disaster" &&
          this.$screenOperationAuthority(ScreenId.SF1405, ScreenId.SF1406)
            .screenId == ScreenId.SF1406) ||
        (this.$route.path == "/oil" && this.hasOilEditAuth()) ||
        PATH_INFO[this.$route.path].doNeedWarning
      ) {
        // 警告ダイアログの表示を設定
        window.onbeforeunload = function(e) {
          e.preventDefault();
          return "";
        };
        // キャンセルされた場合、2秒後に警告ダイアログの表示を解除
        window.setTimeout(function() {
          window.onbeforeunload = null;
        }, 2000);
      }
    },

    /**
     * パンくずリストの定義を生成する
     */
    generateBreadCrumb() {
      // パンくずの初期値
      const breadCrumbs = [
        {
          href: "/",
          text: "トップ",
          disabled: false
        }
      ];

      // 表示フラグを立てる
      this.showBreadCrumb = true;

      // URLを分解
      const allPath = this.$route.path.split("/");
      // 先頭は空文字固定なので削除
      allPath.shift();

      // 先頭が空文字の場合、トップ画面用のパンくずを返す
      if ("" === allPath[0]) {
        // リンクは非活性に
        breadCrumbs[0].disabled = true;
        return breadCrumbs;
      }
      // 先頭がerror, seamless-loginの場合、表示フラグをfalseにして処理を終了
      if ("error" === allPath[0] || "seamless-login" === allPath[0]) {
        // リンクは非活性に
        this.showBreadCrumb = false;
        return breadCrumbs;
      }

      // メニュー画面をパンくずに追加
      breadCrumbs.push({
        href: "/aggregation-menu",
        text: "メニュー",
        disabled: false
      });

      let appendPath = "";
      for (let index = 0; index < allPath.length; index++) {
        const path = allPath[index];
        // パスが空白 or メニュー画面の場合、処理を行わない
        if ("" === path || "aggregation-menu" === path) {
          break;
        }
        // メニュー画面以外の場合はリンクを追加する
        appendPath += "/" + path;
        // 例外的に facility-status の場合は以降の処理を行わない
        if ("facility-status" === path) {
          continue;
        }
        // 例外的に /car/register/web配下 の場合は以降の処理を行わない
        if (appendPath.startsWith("/car/register/web")) {
          continue;
        }
        const map = {
          href: appendPath,
          text: getScreenName(appendPath),
          disabled: false
        };
        breadCrumbs.push(map);
      }

      // 現在の画面のパンくずは非活性に
      breadCrumbs[breadCrumbs.length - 1].disabled = true;

      return breadCrumbs;
    },

    /**
     * OIL画面の編集権限を持っているか
     */
    hasOilEditAuth() {
      const data = this.$screenAuthority.find(auth => {
        const screenCrud = ScreenCrud.factory(auth.screen_crud);
        return auth.screen_id === ScreenId.SF2001 && screenCrud.isOperation();
      });

      if (data) {
        return true;
      } else {
        return false;
      }
    }
  }
};

// パスと画面名の対応表
// 同一URLで警告必要／不要の両パターンが存在する場合、falseにする
const PATH_INFO = {
  "/": { title: "トップ", doNeedWarning: false },
  "/aggregation-menu": { title: "メニュー", doNeedWarning: false },
  "/disaster": { title: "災害名称一覧", doNeedWarning: false },
  "/evacuation-order": { title: "住民向け広報一覧", doNeedWarning: false },
  "/evacuation-order/push-notification-view": {
    title: "住民向け広報参照（プッシュ通知）",
    doNeedWarning: false
  },
  "/evacuation-order/push-notification-edit": {
    title: "住民向け広報登録・編集（プッシュ通知）",
    doNeedWarning: true
  },
  "/evacuation-order/push-notification-duplicate": {
    title: "住民向け広報登録・編集（プッシュ通知）",
    doNeedWarning: true
  },
  "/evacuation-order/push-notification-create": {
    title: "住民向け広報登録・編集（プッシュ通知）",
    doNeedWarning: true
  },
  "/evacuation-order/emergency-mail-view": {
    title: "住民向け広報参照（緊急速報メール）",
    doNeedWarning: false
  },
  "/evacuation-order/emergency-mail-edit": {
    title: "住民向け広報登録・編集（緊急速報メール）",
    doNeedWarning: true
  },
  "/evacuation-order/emergency-mail-duplicate": {
    title: "住民向け広報登録・編集（緊急速報メール）",
    doNeedWarning: true
  },
  "/evacuation-order/emergency-mail-create": {
    title: "住民向け広報登録・編集（緊急速報メール）",
    doNeedWarning: true
  },
  "/chronology": { title: "クロノロジー情報一覧", doNeedWarning: false },
  "/facility-qr-code": {
    title: "要配慮者施設ＱＲコード発行単位登録",
    doNeedWarning: false
  },
  "/facility-qr-code/register": {
    title: "要配慮者施設ＱＲコード登録",
    doNeedWarning: false
  },
  "/facility-qr-code/issue": {
    title: "要配慮者施設ＱＲコード発行対象一覧",
    doNeedWarning: false
  },
  "/school-qr-code": {
    title: "学校ＱＲコード発行単位登録",
    doNeedWarning: false
  },
  "/school-qr-code/register": {
    title: "学校ＱＲコード登録",
    doNeedWarning: false
  },
  "/school-qr-code/issue": {
    title: "学校ＱＲコード発行対象一覧",
    doNeedWarning: false
  },
  "/temporary-qr-code": {
    title: "仮ＱＲコード発行単位登録",
    doNeedWarning: false
  },
  "/temporary-qr-code/register": {
    title: "仮ＱＲコード登録",
    doNeedWarning: false
  },
  "/temporary-qr-code/issue": {
    title: "仮ＱＲコード発行対象一覧",
    doNeedWarning: false
  },
  "/bus-qr-code": { title: "バスＱＲコード印刷", doNeedWarning: false },
  "/stable-iodine": { title: "安定ヨウ素剤配布状況一覧", doNeedWarning: false },
  "/stable-iodine-personal": {
    title: "安定ヨウ素剤事前配布状況個人検索",
    doNeedWarning: false
  },
  "/user": { title: "ユーザ情報一覧", doNeedWarning: false },
  "/user-authorization-operation": {
    title: "操作権限付与一覧",
    doNeedWarning: false
  },
  "/shelter-status": { title: "避難所状況一覧", doNeedWarning: false },
  "/evacuee-status": { title: "避難者検索", doNeedWarning: false },
  "/facility-status/nuclear-disaster": {
    title: "原子力災害関係施設状況一覧",
    doNeedWarning: false
  },
  "/facility-status/vulner-people": {
    title: "要配慮者施設状況一覧",
    doNeedWarning: false
  },
  "/manager-order": { title: "防災関係者向け広報一覧", doNeedWarning: false },
  "/manager-order/view": {
    title: "防災関係者向け広報参照",
    doNeedWarning: false
  },
  "/manager-order/create": {
    title: "防災関係者向け広報登録・編集",
    doNeedWarning: true
  },
  "/manager-order/edit": {
    title: "防災関係者向け広報登録・編集",
    doNeedWarning: true
  },
  "/manager-order/duplicate": {
    title: "防災関係者向け広報登録・編集",
    doNeedWarning: true
  },
  "/lalert": { title: "Lアラート一覧", doNeedWarning: false },
  "/lalert/lalert-notification-view": {
    title: "Lアラート参照（お知らせ）",
    doNeedWarning: false //TODO doNeedWarningチェック必要
  },
  "/lalert/lalert-notification-create": {
    title: "Lアラート登録・編集（お知らせ）",
    doNeedWarning: true
  },
  "/lalert/lalert-notification-edit": {
    title: "Lアラート登録・編集（お知らせ）",
    doNeedWarning: true
  },
  "/lalert/lalert-notification-duplicate": {
    title: "Lアラート登録・編集（お知らせ）",
    doNeedWarning: true
  },
  "/lalert/lalert-evacuation-view": {
    title: "Lアラート参照（避難情報）",
    doNeedWarning: false
  },
  "/lalert/lalert-evacuation-create": {
    title: "Lアラート登録・編集（避難情報）",
    doNeedWarning: true
  },
  "/lalert/lalert-evacuation-edit": {
    title: "Lアラート登録・編集（避難情報）",
    doNeedWarning: true
  },
  "/lalert/lalert-evacuation-duplicate": {
    title: "Lアラート登録・編集（避難情報）",
    doNeedWarning: true
  },
  "/equipment-deploy-register": {
    title: "防災資機材の配備状況取込",
    doNeedWarning: false
  },
  "/stable-iodine-register": {
    title: "安定ヨウ素剤の事前配布状況取込",
    doNeedWarning: false
  },
  "/inspection-result-list": {
    title: "避難退域時検査場所状況表示",
    doNeedWarning: false
  },
  "/disaster-prevention-fa-mst": {
    title: "原子力災害関係施設マスタ一覧",
    doNeedWarning: false
  },
  "/vulner-people-fa-mst": {
    title: "要配慮者施設マスタ一覧",
    doNeedWarning: false
  },
  "/disaster-fixedphrase-mst": {
    title: "災害配信定型文マスタ一覧",
    doNeedWarning: false
  },
  "/info-guide-mst": {
    title: "情報ガイドマスタ一覧",
    doNeedWarning: false
  },
  "/autonomy-mst": {
    title: "自治会マスタ一覧",
    doNeedWarning: false
  },
  "/oil": { title: "OIL一覧", doNeedWarning: true },
  "/car": { title: "管理車両一覧", doNeedWarning: false },
  "/car/register": { title: "管理車両詳細", doNeedWarning: false }
};

const getScreenName = key => {
  let screenName = PATH_INFO[key].title;
  if (typeof screenName == "undefined" || screenName == "") {
    screenName = key;
  }
  return screenName;
};
</script>

<style>
.v-breadcrumbs {
  padding-bottom: 0.5rem;
}
</style>
